import { inject, Injectable } from '@angular/core';
import { defer, from, map, Observable, of, switchMap, throwError } from 'rxjs';
import { PromptFunctions } from '../cloud-functions/prompt.functions';
import { Prompt } from '../models/prompt';
import { PromptImportResponse } from '../models/prompt-import';

@Injectable({
    providedIn: 'root',
})
export class PromptImportExportService {
    private readonly promptFunctions = inject(PromptFunctions);

    public importPrompts(directoryUid: string, jsonFile: File): Observable<PromptImportResponse> {
        return from(jsonFile.text()).pipe(
            map((fileContent) => {
                try {
                    const rawPrompts = JSON.parse(fileContent);
                    return Array.isArray(rawPrompts) ? rawPrompts : [rawPrompts];
                } catch (error) {
                    throw new Error(
                        'Die JSON-Datei enthält ungültiges Format: ' +
                            (error instanceof Error ? error.message : String(error)),
                    );
                }
            }),
            switchMap((prompts) =>
                this.promptFunctions.importPrompts({
                    directoryUid,
                    prompts,
                }),
            ),
        );
    }

    public exportPrompts(
        prompts: Prompt[],
        filename = `prompts-export-${new Date().toISOString().slice(0, 19).replaceAll(':', '-')}.json`,
    ): Observable<string> {
        if (!prompts?.length) {
            return throwError(() => new Error('Keine Prompts zum Exportieren.'));
        }

        return defer(() => {
            const stripped = prompts.map((prompt) => ({
                name: prompt.name,
                text: prompt.text,
                ...(prompt.description?.trim() ? { description: prompt.description } : {}),
                ...(prompt.tags?.length ? { tags: prompt.tags } : {}),
            }));

            const json = JSON.stringify(
                stripped,
                (_key, fieldValue) => (fieldValue instanceof Date ? fieldValue.toISOString() : fieldValue),
                2,
            );

            if (!globalThis.window?.document) {
                throw new Error('Nicht im Browser-Umfeld ausgeführt');
            }

            const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                a.remove();
                URL.revokeObjectURL(url);
            }, 1000);

            return of(filename);
        });
    }
}
